import { TRACKING_QUERY_FIELDS, trackingQueryParameters } from '@vendepay/contracts';
import type { CheckoutUtmAttribution, CheckoutTrackingIdentifiers } from './attribution';
export const FUNNEL_HANDOFF_PARAM = 'vp_handoff';
export const FUNNEL_NODE_PARAM = 'vp_node';
export const FUNNEL_COMPLETE_PARAM = 'vp_complete';
const FUNNEL_HANDOFF_STORAGE_KEY = 'vendepay:funnel-handoff';

export interface FunnelHandoff {
  nodeId: string;
  handoffId: string;
}

export interface FunnelCompletion {
  checkoutId: string;
  saleId: string;
  handoffId: string;
}

export function readFunnelCompletion(hash: string): FunnelCompletion | null {
  if (!isCompleteHandoff(hash)) return null;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const checkoutId = params.get('vp_checkout')?.trim() ?? '';
  const saleId = params.get('vp_sale')?.trim() ?? '';
  const handoffId = params.get(FUNNEL_HANDOFF_PARAM)?.trim() ?? '';
  return checkoutId && saleId && handoffId ? { checkoutId, saleId, handoffId } : null;
}

interface FunnelHandoffStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function readFunnelHandoff(hash: string): FunnelHandoff | null {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const nodeId = params.get(FUNNEL_NODE_PARAM)?.trim() ?? '';
  const handoffId = params.get(FUNNEL_HANDOFF_PARAM)?.trim() ?? '';
  return nodeId.length > 0 && handoffId.length > 0 ? { nodeId, handoffId } : null;
}

export function restoreFunnelHandoff(storage: FunnelHandoffStorage = window.sessionStorage): FunnelHandoff | null {
  try {
    const saved = JSON.parse(storage.getItem(FUNNEL_HANDOFF_STORAGE_KEY) ?? 'null') as unknown;
    if (typeof saved !== 'object' || saved === null) return null;
    const nodeId = 'nodeId' in saved && typeof saved.nodeId === 'string' ? saved.nodeId.trim() : '';
    const handoffId = 'handoffId' in saved && typeof saved.handoffId === 'string' ? saved.handoffId.trim() : '';
    return nodeId.length > 0 && handoffId.length > 0 ? { nodeId, handoffId } : null;
  } catch {
    return null;
  }
}

export function persistFunnelHandoff(
  handoff: FunnelHandoff,
  storage: FunnelHandoffStorage = window.sessionStorage,
): void {
  try {
    storage.setItem(FUNNEL_HANDOFF_STORAGE_KEY, JSON.stringify(handoff));
  } catch {
    return;
  }
}

export function clearFunnelHandoff(storage: FunnelHandoffStorage = window.sessionStorage): void {
  try {
    storage.removeItem(FUNNEL_HANDOFF_STORAGE_KEY);
  } catch {
    return;
  }
}

export function isCompleteHandoff(hash: string): boolean {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  return params.get(FUNNEL_COMPLETE_PARAM) === '1';
}

export function externalFunnelUrl(rawUrl: string, nodeId: string, handoffId: string, saleId: string, tracking = funnelTrackingParameters()): string | null {
  const target = httpsUrl(rawUrl);
  if (target === null || target.hash.length > 0) return null;
  appendTracking(target, tracking);
  target.searchParams.set('saleId', saleId);
  target.hash = handoffHash(nodeId, handoffId);
  return target.toString();
}

export function hostedFunnelUrl(origin: string, nodeId: string, handoffId: string, tracking = funnelTrackingParameters()): string {
  const target = new URL('/f', origin);
  appendTracking(target, tracking);
  target.hash = handoffHash(nodeId, handoffId);
  return target.toString();
}

export function completeFunnelUrl(origin: string): string {
  const target = new URL('/f', origin);
  target.hash = `${FUNNEL_COMPLETE_PARAM}=1`;
  return target.toString();
}

export function thankYouUrl(rawUrl: string, saleId: string, tracking = funnelTrackingParameters()): string | null {
  const target = httpsUrl(rawUrl);
  if (target === null) return null;
  appendTracking(target, tracking);
  target.searchParams.set('saleId', saleId);
  return target.toString();
}

function handoffHash(nodeId: string, handoffId: string): string {
  const params = new URLSearchParams();
  params.set(FUNNEL_NODE_PARAM, nodeId);
  params.set(FUNNEL_HANDOFF_PARAM, handoffId);
  return params.toString();
}

function httpsUrl(rawUrl: string): URL | null {
  let target: URL;
  try {
    target = new URL(rawUrl.trim());
  } catch {
    return null;
  }
  return target.protocol === 'https:' ? target : null;
}

export function funnelTrackingParameters(utm?: CheckoutUtmAttribution, ids?: CheckoutTrackingIdentifiers): Record<string, string> {
  const values: Record<string, string> = {};
  const search = new URLSearchParams(window.location.search);
  for (const key of [...TRACKING_QUERY_FIELDS.map(([key]) => key), 'fbclid', 'ttclid', 'gclid']) {
    const value = search.get(key)?.trim();
    if (value) values[key] = value;
  }
  return { ...values, ...trackingQueryParameters(utm, ids) };
}

function appendTracking(target: URL, values: Record<string, string>): void {
  for (const key of [...TRACKING_QUERY_FIELDS.map(([key]) => key), 'fbclid', 'ttclid', 'gclid']) {
    const value = values[key]?.trim();
    if (value) target.searchParams.set(key, value);
  }
}
